"use client";

import { cn } from "@/lib/utils";
import { useEffect, useRef, useState, type CSSProperties } from "react";

/**
 * Promo Pill 1 — the "Admissions closing soon" badge, ported from BDU v1's
 * `components/ui/BreathingBadge.tsx` (live on bduedu.in).
 *
 * REFERENCE GEOMETRY — copied exactly, so do not "tidy" these numbers:
 *   ring  .......  rounded-[50px] border (1px) p-[6px], breathing scale
 *   fill  .......  rounded-[50px] px-[10px] py-1, w-[300px] on phones → w-auto ≥768px
 *   text  .......  12px/100% semibold → 16px/26px ≥768px
 * BDU v1's `md:` is the 768px default, so every responsive step here is min-[768px].
 * The 1px ring plus 6px gap is what makes the outer yellow read as a thin halo rather
 * than the thick double-lozenge a border-2 gives.
 *
 * PLACEMENT — the badge floats over the TOP OF THE NEXT WIDGET, a short gap below it,
 * and then HOLDS THAT SPOT ON SCREEN while the page scrolls under it, exactly like
 * BDU's `fixed top-[77px] md:top-[119px]` badge.
 *
 * Getting there without a reload flash takes two steps, and both matter:
 *
 *  1. FIRST PAINT IS PURE CSS. This widget's in-flow wrapper is zero-height and
 *     `relative`, sitting exactly at the boundary between the pill's slot and the next
 *     widget — so an absolute child at `top: TOP_GAP_PX` is already "TOP_GAP_PX below
 *     the top of the next widget", correct in the very first server paint with no JS.
 *  2. THEN IT SWITCHES TO `fixed`, using a top measured in DOCUMENT space
 *     (`rect.top + scrollY`). Document space is the whole trick: it is scroll-
 *     invariant, so the value means the same thing no matter where the page sits, and
 *     at scrollTop 0 it lands on the exact pixel step 1 already painted — the handover
 *     is invisible.
 *
 * Do NOT collapse this to a single measured `fixed` top. Measuring can only happen
 * after hydration, before the header's logo/images have loaded, so the first reading
 * is too small and the pill visibly drops down the page once they land. That is the
 * reload jump; step 1 is what hides it. `load` plus a ResizeObserver re-measure as
 * those late images change the header's height.
 *
 * z-[85] is Header 6's featured-pill layer: above page content (which tops out around
 * z-50) so the pill is never buried, below the sticky nav/header (z-[90]/z-[100]).
 *
 * ADMIN PREVIEW — it renders inline while `isPreview` is set, and that flag MUST get
 * through. PreviewRenderer scales its content box with `transform: scale(...)`, and a
 * transform makes an element the containing block for `position: fixed` descendants.
 * So when the editor wrongly took the overlay branch, the pill's measured top (an
 * offset into the whole admin document, easily four figures) was applied INSIDE that
 * box, and the pane's `scrollHeight` grew to match — the giant empty band above the
 * pill in the builder. Inline rendering avoids the transform trap entirely.
 *
 * Blank `text` renders nothing at all — an unconfigured placement stays invisible
 * rather than showing an empty ring.
 */

/** BDU's badge clears the header by 12px on desktop, 13px on phones. */
const TOP_GAP_PX = 12;

/** Height of whatever sticky chrome is pinned to the top, or 0 when there is none. */
function readStickyChromeHeight() {
  const cs = getComputedStyle(document.documentElement);
  return [
    "--header4-sticky-height",
    "--nav3-sticky-height",
    "--nav4-sticky-height",
  ].reduce((total, name) => {
    const px = parseFloat(cs.getPropertyValue(name));
    return total + (Number.isFinite(px) ? px : 0);
  }, 0);
}

/** Column names are the shared `header` table's, since this is a Header variant. */
interface PromoPillData {
  featured_pill_text?: string;
  featured_pill_text_color?: string;
  featured_pill_bg_color?: string;
  featured_pill_border_color?: string;
  featured_pill_sticky?: boolean | number;
  featured_pill_enable_animation?: boolean | number;
  featured_pill_link_url?: string | null;
  status?: boolean | number;
}

/** CMS booleans arrive as true/false, 1/0 or "1"/"0" depending on the write path. */
function isOn(value: boolean | number | string | undefined | null, fallback = true) {
  if (value === undefined || value === null) return fallback;
  if (value === true || value === 1) return true;
  if (value === false || value === 0) return false;
  const s = String(value).trim().toLowerCase();
  if (s === "false" || s === "0" || s === "no") return false;
  if (s === "true" || s === "1" || s === "yes") return true;
  return fallback;
}

function PromoPill1({
  data,
  isCmsPreview,
}: {
  data: any;
  /** PreviewRenderer passes this as a sibling of `data` on every widget. */
  isCmsPreview?: boolean;
}) {
  const raw = data as Record<string, unknown> | undefined;
  // Both spellings, because preparePreviewData nests isPreview inside `data` while
  // PreviewRenderer passes isCmsPreview alongside it. Missing either one drops the
  // editor into the live overlay branch — see the ADMIN PREVIEW note above.
  const isPreview = Boolean(raw?.isPreview ?? raw?.is_preview ?? isCmsPreview);
  const sectionData = (raw?.data ?? raw) as PromoPillData | null;

  const {
    featured_pill_text: text = "",
    featured_pill_text_color: text_color = "#222023",
    featured_pill_bg_color: background_color = "#F4F217",
    featured_pill_border_color: border_color = "#F4F217",
    featured_pill_link_url: link_url = null,
    status,
  } = sectionData ?? {};

  const show = isOn(status) && Boolean(String(text ?? "").trim());
  /** On: overlay the next widget, adding no height. Off: sit inline in the flow. */
  const overlay =
    Boolean(sectionData) &&
    show &&
    isOn(sectionData?.featured_pill_sticky) &&
    !isPreview;
  const animate = isOn(sectionData?.featured_pill_enable_animation);

  /** The in-flow, zero-height anchor. Its top IS the next widget's top. */
  const anchorRef = useRef<HTMLDivElement | null>(null);
  // null on the server and in the first client render, so hydration matches and the
  // load path stays pure CSS. Document-space, so it does not move when the page does.
  const [anchorDocTop, setAnchorDocTop] = useState<number | null>(null);

  useEffect(() => {
    const anchor = anchorRef.current;
    if (!overlay || !anchor) return;

    let frame = 0;

    const measure = () => {
      frame = 0;
      const docTop = anchor.getBoundingClientRect().top + window.scrollY;
      // Never let the pill settle above the sticky chrome it hangs below.
      setAnchorDocTop(Math.max(readStickyChromeHeight(), docTop));
    };

    const schedule = () => {
      if (frame) return;
      frame = requestAnimationFrame(measure);
    };

    schedule();
    // Header logos and banners land after hydration and move the anchor's document y.
    window.addEventListener("load", schedule);
    window.addEventListener("resize", schedule);
    const observer = new ResizeObserver(schedule);
    observer.observe(document.body);

    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("load", schedule);
      window.removeEventListener("resize", schedule);
      observer.disconnect();
    };
  }, [overlay]);

  if (!sectionData || !show) return null;

  const inner = (
    <div
      className={cn(
        "inline-block rounded-[50px] border border-solid p-[6px]",
        animate && "animate-promo-pill-breathing",
      )}
      style={{ borderColor: border_color }}
    >
      <div
        className="flex w-[300px] items-center justify-center rounded-[50px] px-[10px] py-1 text-center text-[12px] font-semibold tracking-normal min-[768px]:w-auto min-[768px]:text-[16px]"
        style={{ backgroundColor: background_color, color: text_color }}
      >
        <div className="leading-[100%] min-[768px]:leading-[26px]">{text}</div>
      </div>
    </div>
  );

  const content = link_url ? (
    <a href={link_url} className="pointer-events-auto">
      {inner}
    </a>
  ) : (
    inner
  );

  /** Transparent so no section background paints a band behind the floating pill. */
  const transparentRowClass = cn(
    "flex w-full items-center justify-center",
    "border-0 !bg-transparent shadow-none ring-0 outline-none [background-image:none]",
  );

  if (overlay) {
    return (
      <div
        ref={anchorRef}
        className="pointer-events-none relative h-0 w-full min-w-0 shrink-0 overflow-visible"
      >
        <div
          className={cn(
            transparentRowClass,
            "pointer-events-none left-0 right-0 z-[85]",
            // Absolute only until measured — see step 1 of PLACEMENT above.
            anchorDocTop === null ? "absolute" : "fixed",
          )}
          style={
            {
              top:
                anchorDocTop === null
                  ? `${TOP_GAP_PX}px`
                  : `${anchorDocTop + TOP_GAP_PX}px`,
              backgroundColor: "transparent",
              backgroundImage: "none",
            } as CSSProperties
          }
        >
          <div className="pointer-events-auto">{content}</div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(transparentRowClass, "py-1.5 min-[768px]:py-2")}
      style={{ backgroundColor: "transparent", backgroundImage: "none" }}
    >
      {content}
    </div>
  );
}

export default PromoPill1;
